import React, { ComponentProps, useEffect, useRef, useState } from "react";
import { IModalHandle, Modal } from "../modal/Modal";
import styles from "./TagComponent.module.scss";
import { Button, ButtonVariant } from "../button/Button";
import XIconThemed from "@src/assets/general-icons/XIconThemed";
import { tagDifference } from "@src/utils/array.utils";
import { MyTooltip } from "../tooltip/Tooltip";
import { CirclePlus, Tag, Tags } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { eventActions, pricingActions, systemActions } from "@src/store";
import { getSuggestedTags } from "@src/store/system/suggestedTags";
import { getInventoryCacheEventId } from "@src/store/pricing/queryEventInventoryAllSlice";
import { filtersConfig, editCustomComponents, OptionType } from "../utils/reactSelectUtils";
import Select, { SingleValue } from "react-select";
import { MixpanelCategory, mixpanelTrack } from "../utils/mixPanelUtils";
import EventContextHeader, { TContextListing } from "@src/components/eventContextHeader/EventContextHeader";

type TagComponentType = {
	itemId: Array<number>;
	childIds: Array<number>;
	type: "event" | "inventory";
	originalTags: Array<string>;
	modalHeader?: string;
	onComplete?: () => void;
	eventHeaderListings?: Array<TContextListing>;
} & ComponentProps<"div">;

type TagComponentTypeExpanded = TagComponentType & {
	closeModal?: () => void;
};

export const TagComponent = ({
	itemId,
	childIds,
	type,
	originalTags,
	modalHeader,
	closeModal,
	onComplete,
	eventHeaderListings,
}: TagComponentTypeExpanded) => {
	const [loading, setLoading] = useState(false);
	const [currentTags, setCurrentTags] = useState(originalTags);
	const originalTagsRef = useRef(originalTags);
	const [text, setText] = useState("");
	const dispatch = useAppDispatch();

	const theme = useAppSelector((state) => state.settings.theme);

	const { data, loading: suggestedTagsLoading } = useAppSelector((state) => state.system.suggestedTags);
	const inventoryCacheEventId = useAppSelector(getInventoryCacheEventId);
	const globalInventoryData = useAppSelector((state) => state.pricing.globalInventory.data);

	const handleAddTag = () => {
		const value = text.trim();
		if (!value || currentTags.includes(value)) return;

		setCurrentTags((prev) => [...prev, value]);
		setText("");
	};

	const tagOptions = (data || []).map((x: string) => ({
		label: x,
		value: x,
	}));

	const removeTag = (tag: string) => {
		setCurrentTags((prev) => prev.filter((x) => x !== tag));
	};

	const onCancelOrXButtonClick = () => {
		onComplete?.();
		closeModal?.();
	};

	const handleSubmit = async () => {
		try {
			setLoading(true);
			const { added, removed } = tagDifference(originalTagsRef.current, currentTags);

			if (type === "event") {
				if (added.length > 0) {
					await dispatch(
						systemActions.addEventTags({
							EventIds: itemId,
							TagDescriptions: added,
						}),
					);
				}
				if (removed.length > 0) {
					const allEventTags = await dispatch(systemActions.getEventTags({ eventIds: itemId })).unwrap();
					const tagsToDelete = allEventTags.filter((x) => removed.includes(x.TagDesc));
					await dispatch(systemActions.removeEventTag({ tagIds: tagsToDelete.map((x) => x.TagId) }));
				}

				dispatch(pricingActions.updateEventItemsTags({ itemIds: itemId, tags: currentTags }));
			}

			if (type === "inventory") {
				if (added.length > 0) {
					await dispatch(
						systemActions.addItemTags({
							ItemIds: childIds,
							TagDescriptions: added,
						}),
					);
				}
				if (removed.length > 0) {
					const allListingTags = await dispatch(systemActions.getListingTags({ listingIds: itemId })).unwrap();
					const tagsToDelete = allListingTags.filter((x) => removed.includes(x.TagDesc));
					await dispatch(systemActions.removeTag({ tagIds: tagsToDelete.map((x) => x.TagId) }));
				}
				dispatch(
					pricingActions.updateEventInventoryItemsTags({
						itemIds: itemId,
						tags: currentTags,
						eventId: inventoryCacheEventId,
					}),
				);
				if (globalInventoryData.length) {
					const Tags = currentTags.length > 0 ? currentTags.join("|") : null;
					dispatch(
						pricingActions.updateGlobalInventory({
							data: globalInventoryData.map((x) => (itemId.includes(x.Listing_ID) ? { ...x, Tags } : x)),
						}),
					);
				}
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
			closeModal?.();
			onComplete?.();
		}
	};

	useEffect(() => {
		if (text.length < 1) return;
		const res = dispatch(getSuggestedTags({ suggestion: text }));

		return () => {
			res.abort();
		};
	}, [dispatch, text]);

	return (
		<div className={styles.root}>
			<div className={styles.header}>
				<div>{modalHeader ?? "No headline"}</div>
				<div>
					<XIconThemed onClick={onCancelOrXButtonClick} color="#fff" />
				</div>
			</div>
			{eventHeaderListings?.length ? <EventContextHeader padding listings={eventHeaderListings} /> : null}
			<div className={styles.content}>
				<div>Edit tag</div>
				<div className={styles.searchRow}>
					<Select<OptionType, false>
						value={null}
						isLoading={suggestedTagsLoading}
						inputValue={text}
						onInputChange={(val, meta) => {
							if (meta.action === "input-change") {
								setText(val);
							}
						}}
						onChange={(option: SingleValue<OptionType>) => {
							if (!option) return;
							const value = String(option.value).trim();
							if (value && !currentTags.includes(value)) {
								setCurrentTags((prev) => [...prev, value]);
								setText("");
							}
						}}
						options={tagOptions}
						isSearchable
						placeholder="Start typing..."
						styles={filtersConfig("", theme)}
						components={editCustomComponents}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								handleAddTag();
								e.preventDefault();
							}
						}}
					/>
					<Button onClick={handleAddTag} variant={ButtonVariant.Secondary}>
						Add
					</Button>
				</div>

				<div className={styles.tagContainer}>
					{currentTags.map((x) => (
						<div key={x} className={styles.tag}>
							<div className={styles.tagLabel}>{x}</div>
							<div>
								<XIconThemed onClick={() => removeTag(x)} />
							</div>
						</div>
					))}
				</div>
			</div>
			<div className={styles.buttons}>
				<Button variant={ButtonVariant.Secondary} onClick={onCancelOrXButtonClick}>
					Cancel
				</Button>
				<Button disabled={loading} variant={ButtonVariant.Primary} onClick={handleSubmit}>
					Apply
				</Button>
			</div>
		</div>
	);
};

export const TagComponentModal = (props: TagComponentType) => {
	const modalRef = useRef<IModalHandle>(null);
	const originalTags = props.originalTags;
	const hasTags = props.originalTags.length > 0;
	const externalOpenModalId = useAppSelector((state) => state.pricing.selection.openTagModalWithId);
	const dispatch = useAppDispatch();

	const handleExternalModalClose = () => {
		if (externalOpenModalId) {
			dispatch(eventActions.setTagModalId(null));
		}
	};

	const handleMixpanelTrack = () => {
		mixpanelTrack(MixpanelCategory.ADD_EDIT_TAGS);
	};

	const openModal = () => {
		modalRef.current?.openModal();
	};

	const closeModal = () => {
		handleExternalModalClose();
		modalRef.current?.closeModal();
	};

	const tooltipId = `tag_tooltip_id_${props.itemId.join("_")}`;
	const tagIcon = originalTags.length === 1 ? <Tag size={16} /> : <Tags size={16} />;
	const tooltip = (
		<MyTooltip
			id={tooltipId}
			popup={<span>{originalTags.join(", ")}</span>}
			element={<div className={styles.tooltipContainer}>{tagIcon}</div>}
		/>
	);

	const addTagIcon = (
		<div className={styles.tooltipContainer}>
			<CirclePlus size={16} />
		</div>
	);

	useEffect(() => {
		if (!modalRef.current || externalOpenModalId !== props.itemId[0]) return;
		openModal();
	}, [externalOpenModalId, props.itemId]);

	return (
		<Modal
			ref={modalRef}
			customButton={
				<div
					onClick={(e) => {
						openModal();
						handleMixpanelTrack();
						e.stopPropagation();
					}}>
					{hasTags ? tooltip : addTagIcon}
				</div>
			}>
			<TagComponent {...props} closeModal={closeModal} />
		</Modal>
	);
};
